import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { QuoteBadge } from "@/components/Badges";
import { CustomerSection } from "@/components/quote/CustomerSection";
import { ItemsEditor } from "@/components/quote/ItemsEditor";
import { TermsSection } from "@/components/quote/TermsSection";
import { QuoteTotals } from "@/components/quote/QuoteTotals";
import { ShareActions, DirectorActions, EditActions } from "@/components/quote/QuoteActions";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, fmtDateTime } from "@/lib/api";
import { computeTotals, toPayload, EDITABLE } from "@/lib/quote";

const EMPTY_CUSTOMER = { hospital_name: "", contact_person: "", phone_number: "", address: "", city: "Varanasi", state: "Uttar Pradesh", gstin: "" };

const Section = ({ title, children, testid }) => (
  <section className="bg-white rounded-2xl border p-4 sm:p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">{title}</div>
    {children}
  </section>
);

const useQuoteData = (id, leadId) => {
  const [f, setF] = useState(null);
  const [q, setQ] = useState(null);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    if (id) {
      api.get(`/quotations/${id}`).then((r) => {
        const { customer, items, terms, subject, notes, valid_until, lead_id, client_id } = r.data;
        setQ(r.data);
        setF({ customer, items, terms, subject, notes, valid_until: valid_until || "", lead_id, client_id });
      }).catch((e) => toast.error(formatErr(e)));
      return;
    }
    api.get("/company").then(async (r) => {
      let customer = EMPTY_CUSTOMER;
      let subject = "";
      if (leadId) {
        const lead = (await api.get("/leads")).data.find((l) => l.id === leadId);
        if (lead) {
          customer = { ...EMPTY_CUSTOMER, hospital_name: lead.hospital_name, contact_person: lead.contact_person, phone_number: lead.phone_number, city: lead.city || "Varanasi" };
          subject = lead.equipment_inquired ? `Quotation for ${lead.equipment_inquired}` : "";
        }
      }
      const valid = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
      setF({ customer, items: [], terms: r.data.default_terms, subject, notes: "", valid_until: valid, lead_id: leadId || null, client_id: null });
    });
  }, [id, leadId]);
  return { f, setF, q, setQ, products, clients };
};

export default function QuoteBuilder() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const base = pathname.startsWith("/admin") ? "/admin/quotations" : "/app/quotes";
  const { f, setF, q, setQ, products, clients } = useQuoteData(id, sp.get("lead"));
  const [busy, setBusy] = useState("");
  const totals = useMemo(() => computeTotals(f?.items || [], f?.customer?.state), [f]);

  if (!f) return <div className="h-40 rounded-2xl bg-slate-200 animate-pulse" />;
  const status = q?.status || "draft";
  const editable = EDITABLE.includes(status);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (c) setF((p) => ({ ...p, client_id: c.id, customer: { ...p.customer, hospital_name: c.hospital_name, contact_person: c.doctor_contact_person || "", phone_number: c.phone_number || "", address: c.address || "", city: c.city || "Varanasi" } }));
  };
  const canSubmit = f.customer.hospital_name.trim().length >= 2 && f.items.length > 0 && f.items.every((it) => it.name.trim() && Number(it.qty) > 0);

  const save = async (andSubmit) => {
    setBusy(andSubmit ? "submit" : "save");
    try {
      const payload = toPayload(f);
      let saved = id ? (await api.put(`/quotations/${id}`, payload)).data : (await api.post("/quotations", payload)).data;
      if (andSubmit) saved = (await api.post(`/quotations/${saved.id}/submit`)).data;
      toast.success(andSubmit ? (saved.status === "approved" ? "Quotation approved" : "Sent to Director for approval") : "Draft saved");
      setQ(saved);
      if (!id) nav(`${base}/${saved.id}`, { replace: true });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy("");
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this quotation?")) return;
    try {
      await api.delete(`/quotations/${id}`);
      toast.success("Quotation deleted");
      nav(base);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <Link to={base} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="quote-back-link"><ArrowLeft className="h-4 w-4" />Quotations</Link>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-num text-xs text-slate-500" data-testid="quote-number">{q?.quote_no || "New quotation"}</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">{f.customer.hospital_name || "Build Quotation"}</h1>
          {q && <div className="text-xs text-slate-500 mt-1">By {q.created_by_name} · {fmtDateTime(q.created_at)}{q.approved_by_name && ` · Approved by ${q.approved_by_name}`}</div>}
        </div>
        <QuoteBadge status={status} testid="quote-status-badge" />
      </div>
      {q?.director_note && (
        <div className="flex gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="quote-director-note">
          <MessageSquareWarning className="h-4 w-4 mt-0.5 shrink-0" /><span><b>Director:</b> {q.director_note}</span>
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
        <div className="space-y-4">
          <Section title="Customer" testid="quote-customer-section">
            <CustomerSection customer={f.customer} onChange={set("customer")} clients={clients} disabled={!editable} onPickClient={pickClient} />
          </Section>
          <Section title={`Items (${f.items.length})`} testid="quote-items-section">
            <ItemsEditor items={f.items} lines={totals.lines} onChange={set("items")} products={products} disabled={!editable} />
          </Section>
          <Section title="Subject, Validity & Notes">
            <div className="grid sm:grid-cols-[1fr_180px] gap-3">
              <div><Label className="text-xs">Subject</Label><Input value={f.subject} disabled={!editable} onChange={(e) => set("subject")(e.target.value)} data-testid="quote-subject-input" /></div>
              <div><Label className="text-xs">Valid Until</Label><Input type="date" value={f.valid_until || ""} disabled={!editable} onChange={(e) => set("valid_until")(e.target.value)} data-testid="quote-valid-until-input" /></div>
            </div>
            <Label className="text-xs mt-3 block">Notes</Label>
            <Textarea value={f.notes} disabled={!editable} onChange={(e) => set("notes")(e.target.value)} className="text-sm" data-testid="quote-notes-input" />
          </Section>
          <Section title="Terms & Conditions"><TermsSection terms={f.terms} onChange={set("terms")} disabled={!editable} /></Section>
        </div>
        <div className="space-y-3 lg:sticky lg:top-20">
          <QuoteTotals t={totals} />
          {editable && <EditActions onSave={() => save(false)} onSubmit={() => save(true)} onDelete={remove} busy={busy} isNew={!id} isDirector={user.role === "director"} canSubmit={canSubmit} />}
          {status === "pending_approval" && user.role === "director" && <DirectorActions q={q} onDone={setQ} />}
          {status === "pending_approval" && user.role !== "director" && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="quote-awaiting-note">Waiting for Director approval. You will be notified, then you can share the PDF.</div>}
          {status === "approved" && <ShareActions q={q} />}
        </div>
      </div>
    </div>
  );
}
